import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { KeyRound, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../hooks/useAuth';
import { changePassword, ApiError } from '../lib/api';
import { useTranslation } from '../i18n';

const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { logout } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleShow = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t.settings.changePassword.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });

      toast.show(t.settings.changePassword.success, 'success');
      await logout();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : t.settings.changePassword.error,
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center py-6 sm:py-12">
      <div className="w-full max-w-md">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-vivatis-600 dark:hover:text-vivatis-400 transition-colors mb-4"
        >
          <ArrowLeft size={16} />
          {t.settings.changePassword.backToSettings}
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-vivatis-50 dark:bg-vivatis-950/30 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-vivatis-500 dark:text-vivatis-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-anthracite-500 dark:text-gray-100">
                {t.settings.changePassword.title}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.settings.changePassword.description}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-4 py-2 break-words">
                {error}
              </div>
            )}

            <PasswordField
              id="current-password"
              label={t.settings.changePassword.currentPassword}
              value={currentPassword}
              onChange={setCurrentPassword}
              show={showPassword}
              onToggleShow={toggleShow}
              autoComplete="current-password"
            />

            <PasswordField
              id="new-password"
              label={t.settings.changePassword.newPassword}
              value={newPassword}
              onChange={setNewPassword}
              show={showPassword}
              onToggleShow={toggleShow}
              autoComplete="new-password"
              minLength={8}
            />

            <PasswordField
              id="confirm-password"
              label={t.settings.changePassword.confirmPassword}
              value={confirmPassword}
              onChange={setConfirmPassword}
              show={showPassword}
              onToggleShow={toggleShow}
              autoComplete="new-password"
              minLength={8}
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-vivatis-600 text-white font-medium text-sm hover:bg-vivatis-700 active:bg-vivatis-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? t.settings.changePassword.submitting
                : t.settings.changePassword.submit}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  autoComplete,
  minLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  autoComplete: string;
  minLength?: number;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300"
      >
        {label}
      </label>
      <div className="relative">
        <Lock
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          minLength={minLength}
          autoComplete={autoComplete}
          placeholder="••••••••"
          className="w-full pl-10 pr-10 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-vivatis-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label={show ? t.login.hidePassword : t.login.showPassword}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export default ChangePasswordPage;
